import React from "react";
import { ArrowDown, ArrowUp, Minus, type LucideIcon } from "lucide-react";

type Props = {
  label: string;
  value: number | string;
  suffix?: string;
  trend?: number;
  trendInverse?: boolean; // when true, negative trend = good (e.g. missed counts)
  icon?: LucideIcon;
};

export default function MetricCard({
  label,
  value,
  suffix,
  trend,
  trendInverse = false,
  icon: Icon,
}: Props): React.ReactElement {
  const t = trend ?? 0;
  const Up = t > 0;
  const Down = t < 0;
  const good = trendInverse ? Down : Up;
  const bad = trendInverse ? Up : Down;
  const color = t === 0 ? "text-text-muted" : good ? "text-success" : bad ? "text-danger" : "text-text-muted";
  const TrendIcon = t === 0 ? Minus : t > 0 ? ArrowUp : ArrowDown;

  return (
    <div className="card p-4 transition-colors hover:border-border-strong">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-text-secondary">
          {label}
        </p>
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-bg-raised border border-border">
            <Icon className="h-4 w-4 text-text-secondary" />
          </span>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-semibold tracking-tight text-text-primary tabular-nums">
          {value}
        </span>
        {suffix && <span className="text-sm text-text-secondary">{suffix}</span>}
      </div>
      {trend !== undefined && (
        <div className={`mt-2 flex items-center gap-1 text-xs ${color}`}>
          <TrendIcon className="h-3 w-3" />
          <span className="tabular-nums font-medium">
            {t > 0 ? "+" : ""}
            {t}
            {typeof t === "number" && Math.abs(t) < 10 && t % 1 !== 0 ? "" : ""}
            {suffix?.includes("%") ? "%" : ""}
          </span>
          <span className="text-text-muted">vs. 昨日</span>
        </div>
      )}
    </div>
  );
}
